import React from 'react';
import { formatTargetContextLabel, getFirstName } from '../../utils/formatters';

const TaskSelectionModal = ({
  child,
  selectedSubject,
  onClose,
  onBack,
  renderAsPage = false,
  onSelectPracticeSheet,
  onSelectCreateNote,
  onSelectCreateVideo,
  onSelectLessonKit,
}) => {
  const headerContextLabel = formatTargetContextLabel(child, { subject: selectedSubject, grade: child?.grade });
  // Lesson Kit is teacher-only — host components pass `onSelectLessonKit`
  // only when the current user is a coaching/school teacher. The grid
  // grows to 4 columns automatically when the option is present so the
  // existing 3-button layout stays unchanged for parents.
  const gridCols = onSelectLessonKit
    ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4'
    : 'grid-cols-1 sm:grid-cols-3';
  return (
    <div className={renderAsPage ? 'w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8' : 'fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4'}>
      <div className={`bg-gradient-to-br from-sky-50 via-white to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-800 rounded-2xl w-full overflow-hidden border border-blue-100 dark:border-slate-700 ${renderAsPage ? 'shadow-lg' : 'max-w-lg max-h-[90vh] shadow-2xl'}`}>
        <div className="sticky top-0 z-10 p-6 border-b border-blue-100 bg-gradient-to-r from-blue-600 to-indigo-600 text-white relative">
          <div className="flex items-center gap-3 pr-10">
            <div className="h-11 w-11 rounded-xl bg-white/20 flex items-center justify-center text-2xl">🧩</div>
            <div>
              <h2 className="text-xl font-semibold leading-tight">Create Task</h2>
              <p className="text-xs text-white/80">{headerContextLabel ? `For: ${headerContextLabel}` : `For: ${getFirstName(child?.name) || 'selected learner'}`}</p>
            </div>
          </div>
          
          <button
            onClick={onBack || onClose}
            aria-label="Close"
            className="text-white/80 hover:text-white text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-white/15 transition-all absolute right-4 top-4"
          >
            <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="p-6 sm:p-8">
          <div className={`grid ${gridCols} gap-4`}>
            <button
              onClick={onSelectPracticeSheet}
              className="p-4 text-center rounded-xl border-2 border-blue-300 bg-gradient-to-br from-blue-100 to-blue-100 hover:from-blue-200 hover:to-blue-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
              data-testid="select-practice-sheet"
            >
              <div className="text-3xl mb-2">📝</div>
              <h3 className="text-sm sm:text-base font-bold text-blue-700">Create Sheet</h3>
              <p className="mt-1 text-xs text-blue-600">Generate worksheet</p>
            </button>

            <button
              onClick={onSelectCreateNote}
              className="p-4 text-center rounded-xl border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-purple-50 hover:from-purple-100 hover:to-purple-100 transition-all hover:-translate-y-0.5 hover:shadow-md"
              data-testid="select-create-note"
            >
              <div className="text-3xl mb-2">📒</div>
              <h3 className="text-sm sm:text-base font-bold text-indigo-700">Create Notes</h3>
              <p className="mt-1 text-xs text-indigo-600">Open notes generator</p>
            </button>

            <button
              onClick={onSelectCreateVideo}
              className="p-4 text-center rounded-xl border-2 border-yellow-300 bg-gradient-to-br from-yellow-50 to-yellow-100 hover:from-yellow-100 hover:to-yellow-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
              data-testid="select-create-video"
            >
              <div className="text-3xl mb-2">🎬</div>
              <h3 className="text-sm sm:text-base font-bold text-yellow-800">Create Video</h3>
              <p className="mt-1 text-xs text-yellow-700">Teaching slides</p>
            </button>
            {onSelectLessonKit && (
              <button
                onClick={onSelectLessonKit}
                className="p-4 text-center rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-emerald-100 hover:from-emerald-100 hover:to-emerald-200 transition-all hover:-translate-y-0.5 hover:shadow-md"
                data-testid="select-lesson-kit"
              >
                <div className="text-3xl mb-2">🎒</div>
                <h3 className="text-sm sm:text-base font-bold text-emerald-800">Create Lesson Kit</h3>
                <p className="mt-1 text-xs text-emerald-700">Plan, deck, quiz &amp; worksheet</p>
              </button>
            )}
          </div>
        </div>

        <div className="pt-4">
          <button
            onClick={onBack || onClose}
            className="w-full py-2.5 px-4 border border-gray-300 dark:border-slate-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

// Child Insights Modal Component  

export default TaskSelectionModal;
